import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { TRANSLATIONS, SUPPORTED_LANGS, DEFAULT_LANG } from './translations';

const STORAGE_KEY = 'kyloo_lang';

const I18nContext = createContext(null);

function detectInitialLang() {
  try {
    const stored = typeof window !== 'undefined' && localStorage.getItem(STORAGE_KEY);
    if (stored && SUPPORTED_LANGS.includes(stored)) return stored;
    if (typeof navigator !== 'undefined' && navigator.language) {
      const short = navigator.language.toLowerCase().split('-')[0];
      if (SUPPORTED_LANGS.includes(short)) return short;
    }
  } catch (e) {
    // localStorage disabled / SSR / privacy mode — fall back to default.
    console.debug('detectInitialLang failed:', e?.message);
  }
  return DEFAULT_LANG;
}

// Resolve a nested key like "header.search_placeholder" from the dict.
function resolve(dict, key) {
  if (!dict || !key) return undefined;
  return key.split('.').reduce((acc, k) => (acc && acc[k] !== undefined ? acc[k] : undefined), dict);
}

function interpolate(str, vars) {
  if (typeof str !== 'string' || !vars) return str;
  return str.replace(/\{(\w+)\}/g, (m, k) => (vars[k] !== undefined ? String(vars[k]) : m));
}

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(() => detectInitialLang());

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, lang);
      if (typeof document !== 'undefined' && document.documentElement) {
        document.documentElement.lang = lang;
      }
    } catch (e) {
      // Non-blocking: language state is already in memory, storage is best-effort.
      console.debug('Persist lang failed:', e?.message);
    }
  }, [lang]);

  const setLang = useCallback((next) => {
    if (SUPPORTED_LANGS.includes(next)) setLangState(next);
  }, []);

  const t = useCallback(
    (key, vars) => {
      const fromActive = resolve(TRANSLATIONS[lang], key);
      const fromFallback = resolve(TRANSLATIONS[DEFAULT_LANG], key);
      const value = fromActive !== undefined ? fromActive : fromFallback;
      if (value === undefined) return key; // safe fallback: return the key itself
      return interpolate(value, vars);
    },
    [lang],
  );

  const value = useMemo(() => ({ t, lang, setLang }), [t, lang, setLang]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useT() {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    // Safety net: never crash if a component renders outside the provider.
    return {
      t: (k) => k,
      lang: DEFAULT_LANG,
      setLang: () => {},
    };
  }
  return ctx;
}
